/**
 * This file contains a Sentry wrapper that centralizes how handling errors and
 * messages are sent to the Sentry library.
 *
 * We will only send notifications to Sentry if two conditions are fulfilled:
 *  1. pageSettings.analytics is set.
 *  2. Error occurs during Client-side rendering.
 *
 * DO NOT USE THIS DIRECTLY
 *
 * Instead, use `apps/khanacademy/src/logging/log.js` and `Log.error`
 *
 * @example
 * import Log, {Errors} from "@khan/logging";
 *
 * // Exceptions
 * Log.error(
 *      new Error("Error"),
 *      Errors.Internal,
 * );
 * Log.error(
 *      "The thing failed",
 *      Errors.Internal,
 * );
 * Log.error(
 *      "The thing failed",
 *      Errors.Internal,
 *      {cause: new Error("This is the error that happened when it failed")},
 * );
 * Log.error(
 *      new KAError("This is a KA error", Errors.Internal),
 * );
 *
 * // Exceptions with metadata
 * // Each of the above signatures can also specify metadata.
 * // metadata. Metadata within the sentry property can be used to provide
 * // scoped data to sentry like tags, extras, contexts, and fingerprint.
 * // The error kind is always attached to the sentry scope as a tag.
 * Log.error(
 *      "The thing failed",
 *      Errors.Internal,
 *      {
 *          metadata: {
 *          // This metadata is logged during SSR and when client-side logging
 *          // is enabled, but not sent to sentry.
 *          data: 1,
 *          moredata: "something to log",
 *          },
 *          // This metadata is logged as with the previous data, but also
 *          // used to configure the sentry scope.
 *          sentryData: {
 *              // setTag
 *              // setTags
 *              tags: {
 *                  p1: "true",
 *              },
 *              // setFingerprint
 *              fingerprint: ["fingerprint", "bits", "here"],
 *              // setContext
 *              // setContexts
 *              // setExtra
 *              // setExtras
 *              contexts: {
 *                  contextName1: {
 *                      someValue: "Value",
 *                  },
 *                  "Super Context": {
 *                      someValue: "Value",
 *                  },
 *              },
 *          },
 *      },
 * );
 *
 */
// TODO(WEB-1464, somewhatabstract): Delay load the sentry file in a manner
// that allows us to capture failures to load sentry.
import * as Sentry from "@sentry/browser";
import type {Context, Primitive} from "@sentry/core";

import {KAError} from "./ka-error.ts";
import {
    configExtraSentry,
    configSentry,
    type SentryExtraConfig,
} from "./sentry-config.ts";
import {toKAError} from "./to-ka-error.ts";
import {Errors} from "./types.ts";

type SentryAPI = typeof Sentry;

/**
 * Sentry internal reference
 */
let _Sentry: SentryAPI | null | undefined;

/**
 * Whether we try to initialize the library
 */
let _isInitialized = false;

/**
 * We should store a reference of the previous window.onerror handler, in case
 * there's one. That way, we will restore it after we load Sentry's library.
 */
let _prevOnerror: Window["onerror"] = null;

/**
 * We should store a reference of the previous window.onunhandledrejection
 * handler, in case there's one. That way, we will restore it after we load
 * Sentry's library.
 */
let _prevOnunhandledrejection: Window["onunhandledrejection"] = null;

type EagerSentryThen<TIn, TOut> = (cb: (result: TIn) => TOut) => Promise<TOut>;
interface EagerSentryPromise<TIn, TOut> {
    then: EagerSentryThen<TIn, TOut>;
}
const eagerSentryPromisesque = <TIn, TOut>(
    sentry: TIn,
): EagerSentryPromise<TIn, TOut> => {
    const then: EagerSentryThen<TIn, TOut> = (
        cb: (arg1: TIn) => TOut,
    ): Promise<TOut> => Promise.resolve(cb(sentry));
    return {then};
};

// TODO(WEB-1464): Optimize Sentry bundle and make sure we have a fallback in
// case the dynamic import fails to load.
/**
 * Loads the Sentry library and continues with the execution.
 *
 * @returns {Promise<Sentry>} The Sentry module dynamically loaded.
 *
 * @example
 * getSentry().then(Sentry => Sentry.captureMessage("sentry message"))
 */
function getSentry(): Promise<SentryAPI> | EagerSentryPromise<SentryAPI, any> {
    // return cached version eagerly so that things will run within their scope
    // If we don't do this eagerly, then the scope ends before the promise
    // for a capture call within the scope actually resolves, leading to the
    // capture not running within the scope.
    if (_Sentry) {
        return eagerSentryPromisesque<SentryAPI, any>(_Sentry);
    }

    // NOTE(jeresig, WEB-1464): @sentry/browser is actually inside the corelibs
    // bundle so this doesn't really do anything to save us on the download
    // size. We'll want to change this to experiment with loading it
    // dynamically to save on bytes.

    // Sentry is going to setup onerror and onunhandledrejection handlers
    // so we won't need the ones we set up in our init function anymore.
    // We will restore the previous handlers (if any) before we initialize
    // Sentry, to avoid duplicate error reporting.
    window.onerror = _prevOnerror;
    window.onunhandledrejection = _prevOnunhandledrejection;

    // TODO(WEB-1464, somewhatabstract): Delay load the sentry file in a manner
    // that allows us to capture failures to load sentry.
    _Sentry = Sentry;

    configSentry(Sentry);

    // continue with the execution
    return Promise.resolve(Sentry);
}

/**
 * Initializes Sentry only if some error happens.
 *
 * This is exported by log.js for use by our site infrastruction initialization
 * as `initKhanSentry`.
 */
export const init = (options: SentryExtraConfig) => {
    // prevent calling this function twice
    if (_isInitialized) {
        return;
    }

    // Set this now to avoid multiple calls or re-entrance from
    // causing more than one initialization.
    _isInitialized = true;

    // save the original handlers
    // We will restore these when we allow Sentry to initialize itself.
    _prevOnerror = window.onerror;
    _prevOnunhandledrejection = window.onunhandledrejection;

    // Now we setup our own handlers.
    // The first call to one of these will cause Sentry to be loaded and
    // initialized.
    window.onerror = (message, url, line, column, error) =>
        captureException(error);
    window.onunhandledrejection = (event) => captureException(event.reason);

    // Store away extra configuration for Sentry to be applied once we load it.
    configExtraSentry(options);
};

/**
 * Uninitializes Sentry object.
 *
 * Note: This method will only work in tests. It will throw if called outside
 * of testing.
 * @private
 */
export const uninitialize = () => {
    if (process.env.NODE_ENV === "test") {
        _Sentry = null;
        _isInitialized = false;
    } else {
        throw new KAError("Not implemented", Errors.NotImplemented);
    }
};

/**
 * Captures an exception event and sends it to Sentry.
 *
 * @param exception An exception-like object.
 * @returns {Promise<string>} The generated eventId.
 */
export const captureException = (error?: Error): Promise<string> => {
    /**
     * When this is called from outside our centralized logging, it could be
     * given anything, not just a KAError. We need to make sure we cope with
     * that. We could be receiving...
     *   1. KAError
     *   2. A string or Error
     *   3. null
     *   4. Something else
     */
    const sentry = getSentry();
    return new Promise((resolve, reject) => {
        // NOTE: We use `then` instead of async/await because getSentry can
        // return an eager promise (see comment in getSentry).
        sentry.then((Sentry: SentryAPI) => {
            // First, we need to know what kind of thing we have and turn it
            // into a capturable thing.
            const normalizedError =
                // 1. KAError
                error instanceof KAError
                    ? error
                    : // 2. Error or string
                      typeof error === "string" || error instanceof Error
                      ? toKAError(error, Errors.Internal)
                      : // 3. null
                        error == null
                        ? new KAError(
                              "Null captured as exception",
                              Errors.Internal,
                          )
                        : // 4. Something else
                          new KAError(
                              "Non-error value captured as exception",
                              Errors.Internal,
                              {
                                  metadata: {
                                      error,
                                  },
                              },
                          );

            // Now, we can capture the error with sentry.
            resolve(Sentry.captureException(normalizedError));
        });
    });
};

/**
 * Set a tag on the current Sentry scope.
 *
 * For react components, use `useSentryTag`.
 *
 * This is useful in situations where the code that may capture an error does
 * not have access to the information that needs to be tagged on that error.
 * For example, Perseus may capture the error, but the code using Perseus
 * may wish to attach additional information to such an error.
 *
 * Make sure to clear the tag when it is no longer needed.
 *
 * See also KhanSentry.setContext.
 *
 * Note that tags could be overridden if the name clashes with a tag set after
 * a call to this method.
 *
 * @param tag The name of the tag to set.
 * @param value The value of the tag to be set. If `undefined`, the
 * tag will be removed.
 * @returns A promise that resolves when the tag has been set.
 */
export const setTag = (tag: string, value: Primitive): Promise<void> => {
    return new Promise((resolve, reject) => {
        // NOTE: We use `then` instead of async/await because getSentry can
        // return an eager promise (see comment in getSentry).
        getSentry().then((Sentry: SentryAPI) => {
            const scope = Sentry.getCurrentScope();
            scope.setTag(tag, value);
            resolve();
        });
    });
};

/**
 * Set a context on the current Sentry scope.
 *
 * For react components, use `useSentryContext`.
 *
 * This is useful in situations where the code that may capture an error does
 * not have access to the information that needs to be tagged on that error.
 * For example, Perseus may capture the error, but the code using Perseus
 * may wish to attach additional information to such an error.
 *
 * Make sure to clear the context when it is no longer needed.
 *
 * See also KhanSentry.setTag.
 *
 * Note that contexts could be overridden if the name clashes with a context set
 * after a call to this method.
 *
 * @param name The name of the context to set.
 * @param context The values to be set for the context. If `null`, the context
 * will be removed.
 * @returns A promise that resolves when the context has been set.
 */
export const setContext = (
    name: string,
    context: Context | null,
): Promise<void> => {
    return new Promise((resolve, reject) => {
        // NOTE: We use `then` instead of async/await because getSentry can
        // return an eager promise (see comment in getSentry).
        getSentry().then((Sentry: SentryAPI) => {
            const scope = Sentry.getCurrentScope();
            scope.setContext(name, context);
            resolve();
        });
    });
};
